import React, { useState, useEffect } from 'react';
import { specialAssignmentsAPI } from '../../utils/api';
import { getCurrentLocation, reverseGeocode, decimalToDMS } from '../../utils/gpsUtils';
import notificationService from '../../services/notificationService';
import './SpecialAssignment.css';

const SpecialAssignment = () => {
  const [activeTab, setActiveTab] = useState('create'); // 'create', 'my-assignments', 'assigned'
  const [formData, setFormData] = useState({
    siteName: '',
    title: '',
    description: '',
    location: ''
  });
  const [assignments, setAssignments] = useState([]);
  const [assignedToMe, setAssignedToMe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [locationCoords, setLocationCoords] = useState(null);

  useEffect(() => {
    loadAssignments();
  }, [activeTab]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const response = await specialAssignmentsAPI.getMyAssignments();
      const allAssignments = response.data?.data || response.data || [];
      
      // Debug: Log all assignments to see what backend is returning
      console.log('📋 All assignments received:', allAssignments);
      console.log('📋 Total count:', allAssignments.length);
      
      // Separate assignments: created by me vs assigned to me
      const created = allAssignments.filter(a => a.createdBy === 'self' || a.created_by === 'self');
      const assigned = allAssignments.filter(a => 
        a.assignedBy === 'admin' || 
        a.assigned_by === 'admin' || 
        a.createdBy === 'admin' || 
        a.created_by === 'admin'
      );
      
      console.log('✅ Created by me:', created);
      console.log('📩 Assigned to me:', assigned);
      
      setAssignments(created);
      setAssignedToMe(assigned);
    } catch (error) {
      console.error('Error loading assignments:', error);
      showAlert('error', 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCaptureLocation = async () => {
    setFetchingLocation(true);
    try {
      // Get GPS coordinates
      const coords = await getCurrentLocation();
      console.log('GPS Location captured:', coords);
      
      // Store coordinates for potential backend submission
      setLocationCoords({
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy
      });
      
      // Reverse geocode to get address
      const address = await reverseGeocode(coords.latitude, coords.longitude);
      console.log('Reverse geocoded address:', address);
      
      // Update location field with address
      setFormData(prev => ({
        ...prev,
        location: address
      }));
      
      showAlert('success', `Location captured: ${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`);
    } catch (error) {
      console.error('Error capturing location:', error);
      showAlert('error', error.message || 'Failed to capture location');
    } finally {
      setFetchingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.siteName.trim()) {
      showAlert('error', 'Site Name is required');
      return;
    }
    if (!formData.title.trim()) {
      showAlert('error', 'Title is required');
      return;
    }
    if (!formData.description.trim()) {
      showAlert('error', 'Description is required');
      return;
    }
    if (!formData.location.trim()) {
      showAlert('error', 'Location is required');
      return;
    }

    setSubmitting(true);
    try {
      // Prepare submission data with GPS coordinates if available
      const submissionData = {
        ...formData
      };
      
      // Add GPS coordinates if they were captured
      if (locationCoords) {
        submissionData.latitude = locationCoords.latitude;
        submissionData.longitude = locationCoords.longitude;
        if (locationCoords.accuracy) {
          submissionData.locationAccuracy = locationCoords.accuracy;
        }
      }
      
      await specialAssignmentsAPI.create(submissionData);

      // Trigger notification to admin and sub-admins
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      notificationService.triggerAssignmentAction('assignment-submitted', {
        personnelName: currentUser?.name || currentUser?.username || 'Personnel',
        personnelId: currentUser?.id || currentUser?.userId,
        assignmentTitle: formData.title,
        location: formData.location,
        siteName: formData.siteName
      });

      showAlert('success', 'Special assignment submitted successfully!');
      
      // Reset form
      setFormData({
        siteName: '',
        title: '',
        description: '',
        location: ''
      });
      
      // Clear location coordinates
      setLocationCoords(null);
      
      // Reload assignments
      loadAssignments();
    } catch (error) {
      console.error('Error submitting assignment:', error);
      showAlert('error', error.response?.data?.message || 'Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptAssignment = async (id) => {
    try {
      // Get assignment details before accepting
      const assignment = assignedToMe.find(a => a.id === id);
      
      await specialAssignmentsAPI.updateStatus(id, 'in_progress');
      showAlert('success', 'Assignment accepted and moved to In Progress');
      
      // Trigger notification to admins and sub-admins
      if (assignment) {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        notificationService.triggerAssignmentAction('assignment-accepted', {
          personnelName: currentUser?.name || currentUser?.username || 'Personnel',
          personnelId: currentUser?.id || currentUser?.userId,
          assignmentTitle: assignment.title,
        });
      }
      
      loadAssignments();
    } catch (error) {
      console.error('Error accepting assignment:', error);
      showAlert('error', 'Failed to accept assignment');
    }
  };

  const handleRejectAssignment = async (id) => {
    const notes = prompt('Please provide a reason for rejection:');
    if (!notes) return;

    try {
      // Get assignment details before rejecting
      const assignment = assignedToMe.find(a => a.id === id);
      
      await specialAssignmentsAPI.updateStatus(id, 'rejected', notes);
      showAlert('success', 'Assignment rejected');
      
      // Trigger notification to admins and sub-admins
      if (assignment) {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        notificationService.triggerAssignmentAction('assignment-rejected', {
          personnelName: currentUser?.name || currentUser?.username || 'Personnel',
          personnelId: currentUser?.id || currentUser?.userId,
          assignmentTitle: assignment.title,
        });
      }
      
      loadAssignments();
    } catch (error) {
      console.error('Error rejecting assignment:', error);
      showAlert('error', 'Failed to reject assignment');
    }
  };

  const handleCompleteAssignment = async (id) => {
    try {
      // Get assignment details before completing
      const assignment = assignedToMe.find(a => a.id === id);
      
      await specialAssignmentsAPI.updateStatus(id, 'completed');
      showAlert('success', 'Assignment marked as completed. Waiting for admin verification.');
      
      // Trigger notification to admins and sub-admins
      if (assignment) {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        notificationService.triggerAssignmentAction('assignment-completed', {
          personnelName: currentUser?.name || currentUser?.username || 'Personnel',
          personnelId: currentUser?.id || currentUser?.userId,
          assignmentTitle: assignment.title,
        });
      }
      
      loadAssignments();
    } catch (error) {
      console.error('Error completing assignment:', error);
      showAlert('error', 'Failed to mark assignment as completed');
    }
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const viewDetails = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailModal(true);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'pending': 'pending',
      'approved': 'approved',
      'rejected': 'rejected',
      'in_progress': 'in-progress',
      'completed': 'completed',
      'closed': 'closed'
    };
    return statusMap[status] || 'pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending Review',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'closed': 'Closed'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="special-assignment-container">
      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'danger' : alert.type}`}>
          <i className={`fas fa-${alert.type === 'error' ? 'exclamation-circle' : 'check-circle'}`}></i>
          {alert.message}
        </div>
      )}

      <div className="assignment-card">
        <div className="assignment-header">
          <h1 className="assignment-title">Special Assignments</h1>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs-nav">
            <button
              className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
              onClick={() => setActiveTab('create')}
            >
              <i className="fas fa-plus-circle"></i> Create Assignment
            </button>
            <button
              className={`tab-button ${activeTab === 'my-assignments' ? 'active' : ''}`}
              onClick={() => setActiveTab('my-assignments')}
            >
              <i className="fas fa-list"></i> My Submissions
            </button>
            <button
              className={`tab-button ${activeTab === 'assigned' ? 'active' : ''}`}
              onClick={() => setActiveTab('assigned')}
            >
              <i className="fas fa-tasks"></i> Assigned to Me
            </button>
          </div>
        </div>

        {/* Create Assignment Form */}
        {activeTab === 'create' && (
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h2 className="form-section-title">Submit a Special Assignment</h2>

              <div className="form-group">
                <label htmlFor="siteName">
                  Site Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="siteName"
                  name="siteName"
                  className="form-control"
                  value={formData.siteName}
                  onChange={handleInputChange}
                  placeholder="Enter site name"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter assignment title"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">
                  Description <span className="required">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe what you are doing..."
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">
                  Location <span className="required">*</span>
                </label>
                <div className="location-input-wrapper">
                  <button
                    type="button"
                    className="location-icon-btn"
                    onClick={handleCaptureLocation}
                    disabled={submitting || fetchingLocation}
                    title="Capture current location"
                  >
                    {fetchingLocation ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-map-marker-alt"></i>
                    )}
                  </button>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    className="form-control location-input"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder={fetchingLocation ? "Fetching location..." : "Click icon to capture location or type manually"}
                    disabled={submitting}
                  />
                </div>
                {locationCoords && (
                  <small className="location-coords-hint">
                    <i className="fas fa-info-circle"></i>
                    Coordinates: {decimalToDMS(locationCoords.latitude, true)} {decimalToDMS(locationCoords.longitude, false)}
                  </small>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setFormData({ siteName: '', title: '', description: '', location: '' })}
                disabled={submitting}
              >
                <i className="fas fa-undo"></i> Clear
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i> Submit Assignment
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* My Submitted Assignments */}
        {activeTab === 'my-assignments' && (
          <div className="assignment-list">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading assignments...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <i className="fas fa-inbox"></i>
                </div>
                <p className="empty-state-text">No assignments yet</p>
                <p className="empty-state-subtext">Create your first special assignment</p>
              </div>
            ) : (
              assignments.map((assignment) => (
                <div key={assignment.id} className="assignment-item">
                  <div className="assignment-item-header">
                    <div>
                      <h3 className="assignment-item-title">{assignment.title}</h3>
                      <span className={`status-badge ${getStatusBadgeClass(assignment.status)}`}>
                        {getStatusLabel(assignment.status)}
                      </span>
                    </div>
                  </div>
                  <div className="assignment-item-meta">
                    <span><i className="fas fa-map-marker-alt"></i> {assignment.location}</span>
                    <span><i className="fas fa-calendar"></i> {formatDate(assignment.createdAt || assignment.created_at)}</span>
                  </div>
                  <p className="assignment-item-description">
                    {assignment.description.substring(0, 150)}
                    {assignment.description.length > 150 ? '...' : ''}
                  </p>
                  <div className="assignment-item-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => viewDetails(assignment)}
                    >
                      <i className="fas fa-eye"></i> View Details
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Assignments Assigned to Me */}
        {activeTab === 'assigned' && (
          <div className="assignment-list">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading assignments...</p>
              </div>
            ) : assignedToMe.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <i className="fas fa-tasks"></i>
                </div>
                <p className="empty-state-text">No assignments yet</p>
                <p className="empty-state-subtext">You have no assignments from admin</p>
              </div>
            ) : (
              assignedToMe.map((assignment) => (
                <div key={assignment.id} className="assignment-item">
                  <div className="assignment-item-header">
                    <div>
                      <h3 className="assignment-item-title">{assignment.title}</h3>
                      <span className={`status-badge ${getStatusBadgeClass(assignment.status)}`}>
                        {getStatusLabel(assignment.status)}
                      </span>
                    </div>
                  </div>
                  <div className="assignment-item-meta">
                    <span><i className="fas fa-map-marker-alt"></i> {assignment.location}</span>
                    <span><i className="fas fa-calendar"></i> {formatDate(assignment.createdAt || assignment.created_at)}</span>
                    <span><i className="fas fa-user-tie"></i> Assigned by Admin</span>
                  </div>
                  <p className="assignment-item-description">
                    {assignment.description.substring(0, 150)}
                    {assignment.description.length > 150 ? '...' : ''}
                  </p>
                  <div className="assignment-item-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => viewDetails(assignment)}
                    >
                      <i className="fas fa-eye"></i> View Details
                    </button>
                    {assignment.status === 'pending' && (
                      <>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleAcceptAssignment(assignment.id)}
                        >
                          <i className="fas fa-check"></i> Accept
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRejectAssignment(assignment.id)}
                        >
                          <i className="fas fa-times"></i> Reject
                        </button>
                      </>
                    )}
                    {assignment.status === 'in_progress' && (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleCompleteAssignment(assignment.id)}
                      >
                        <i className="fas fa-check-circle"></i> Mark as Completed
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedAssignment && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="modal-title">{selectedAssignment.title}</h2>
                <span className={`status-badge ${getStatusBadgeClass(selectedAssignment.status)}`}>
                  {getStatusLabel(selectedAssignment.status)}
                </span>
              </div>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {/* Description Section */}
              <div className="detail-section">
                <div className="detail-section-label">
                  <i className="fas fa-align-left"></i> Description
                </div>
                <div className="detail-section-content">{selectedAssignment.description}</div>
              </div>

              {/* Assignment Details Grid */}
              <div className="detail-grid">
                <div className="detail-card">
                  <div className="detail-card-content">
                    <div className="detail-card-label"><i className="fas fa-map-marker-alt"></i> Location</div>
                    <div className="detail-card-value">{selectedAssignment.location}</div>
                  </div>
                </div>

                <div className="detail-card">
                  <div className="detail-card-content">
                    <div className="detail-card-label"><i className="fas fa-clock"></i> Submitted On</div>
                    <div className="detail-card-value">{formatDateTime(selectedAssignment.createdAt || selectedAssignment.created_at)}</div>
                  </div>
                </div>

                {(selectedAssignment.updatedAt || selectedAssignment.updated_at) && (
                  <div className="detail-card">
                    <div className="detail-card-content">
                      <div className="detail-card-label"><i className="fas fa-sync-alt"></i> Last Updated</div>
                      <div className="detail-card-value">{formatDate(selectedAssignment.updatedAt || selectedAssignment.updated_at)}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes Section */}
              {selectedAssignment.notes && (
                <div className="detail-section">
                  <div className="detail-section-label">
                    <i className="fas fa-sticky-note"></i> Notes
                  </div>
                  <div className="detail-section-content">{selectedAssignment.notes}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedAssignment.status === 'pending' && selectedAssignment.createdBy === 'admin' && (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      setShowDetailModal(false);
                      handleAcceptAssignment(selectedAssignment.id);
                    }}
                  >
                    <i className="fas fa-check"></i> Accept
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => {
                      setShowDetailModal(false);
                      handleRejectAssignment(selectedAssignment.id);
                    }}
                  >
                    <i className="fas fa-times"></i> Reject
                  </button>
                </>
              )}
              {selectedAssignment.status === 'in_progress' && (
                <button 
                  className="btn btn-success" 
                  onClick={() => {
                    setShowDetailModal(false);
                    handleCompleteAssignment(selectedAssignment.id);
                  }}
                >
                  <i className="fas fa-check-circle"></i> Mark as Completed
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialAssignment;
